import React, { useState, useEffect } from "react";
import { Survey } from "survey-react-ui";
import { Model } from "survey-core";
import "survey-core/survey-core.css";
import { X } from "lucide-react";
import "./PreviewModal.scss";

const PreviewModal = ({ show, onClose, quizTitle, questions, quizType }) => {
  const [surveyModel, setSurveyModel] = useState(null);

  useEffect(() => {
    if (!show || !questions || questions.length === 0) {
      setSurveyModel(null);
      return;
    }

    // Transform questions to Survey.js format
    const elements = questions.map((q, index) => {
      const element = {
        type: q.type,
        name: `q${index + 1}`,
        title: q.text,
        isRequired: false, // For preview, make questions optional
      };

      // Add Arabic title if available
      if (q.arabicText) {
        element.description = q.arabicText;
      }

      // Add choices for question types that need them
      if (["radiogroup", "checkbox", "dropdown"].includes(q.type)) {
        element.choices = q.options?.filter((opt) => opt?.trim() !== "") || [];
      }

      // Handle boolean type (Survey.js uses 'boolean' differently)
      if (q.type === "boolean") {
        element.type = "radiogroup";
        element.choices = ["Yes", "No"];
      }

      return element;
    });

    // Create survey definition - REMOVED timer-related properties
    const surveyDefinition = {
      title: quizTitle || "Quiz Preview",
      pages: [
        {
          elements: elements,
        },
      ],
      showProgressBar: "top",
      showQuestionNumbers: "onPage",
      questionsOnPageMode: "singlePage",
      showCompletedPage: false,
      showPreviewBeforeComplete: "noPreview",
      checkErrorsMode: "onComplete",
      showNavigationButtons: "none", // Remove all navigation buttons
    };

    // Create the model
    const model = new Model(surveyDefinition);

    // Disable completion entirely
    model.showCompleteButton = false;
    model.showPreviewButton = false;
    model.showNavigationButtons = "none";

    // Customize survey for preview
    model.onAfterRenderQuestion.add((survey, options) => {
      // Remove clear button from dropdowns
      if (options.question.getType() === "dropdown") {
        const clearBtn = options.htmlElement.querySelector(
          ".sd-dropdown_clean-button"
        );
        if (clearBtn) {
          clearBtn.remove();
        }
      }
    });

    // Remove any completion functionality
    model.onAfterRenderSurvey.add((survey, options) => {
      // Hide complete button and any navigation buttons
      const completeBtn = options.htmlElement.querySelector(
        ".sv-btn.sv-completebtn"
      );
      const navButtons = options.htmlElement.querySelector(".sv-action-bar");
      const footer = options.htmlElement.querySelector(".sv-body__footer");

      if (completeBtn) completeBtn.style.display = "none";
      if (navButtons) navButtons.style.display = "none";
      if (footer) footer.style.display = "none";
    });

    setSurveyModel(model);
  }, [show, questions, quizTitle]);

  if (!show) return null;

  return (
    <div
      className="preview-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="preview-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="preview-modal-header">
          <div className="preview-modal-header-text">
            <h2>{quizType} Preview</h2>
            <p>This is how your {quizType} will appear to users</p>
          </div>
          <button className="preview-modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Survey Content */}
        <div className="preview-modal-body">
          {surveyModel ? (
            <div className="preview-modal-survey-container">
              <div className="preview-modal-warning">
                <p>
                  <strong>Preview Mode:</strong> This shows exactly how your{" "}
                  {quizType} will look to users. Timer functionality is disabled
                  in preview mode.
                </p>
              </div>
              <Survey model={surveyModel} />
            </div>
          ) : (
            <div className="preview-modal-no-questions">
              <p>No questions available to preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
